import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import NoResultsForFiltersMessage from '../no-results-for-filters-message';
import {
    Box,
    Card,
    Flex,
    Image,
    List,
    ListItem,
    Text,
    useBreakpointValue,
} from '@spothero/ui';
import trackErrorMessageDisplayed from 'segment/events/error-message-displayed';
import {useSelector} from 'react-redux';
import SearchTracking from 'utils/search-tracking';
import {Page} from 'utils/page-utils';
import getParkingType from 'segment/utils/parking-type';
import ErrorUtils from 'utils/error-utils';

const getSegmentProperties = (state, numFilters) => {
    const coreProperties = {
        message: 'No results found',
        errorMessage:
            numFilters > 0
                ? `But don't worry, there are a few things you can try to broaden your search: Consider removing the filters to see all available spots. Zoom out on the map to expand your search area. Sometimes, there are great options just a little further away.`
                : `Don't worry though, we've got a few suggestions to help you out: Try adjusting the dates and times you've selected. If you zoom out on the map, you might find results just outside your initial search area. Consider typing in a different location.`,
        action: numFilters > 0 ? 'remove filters' : 'no available spots',
        screen: 'search',
        errorHeader:
            numFilters > 0
                ? `We couldn't find any nearby spots that match the filters you applied.`
                : `We couldn't find any available spots nearby`,
        currentScreenName: 'search',
    };

    try {
        const {
            city: {
                data: {slug: citySlug},
            },
            destination: {
                data: {airport},
            },
            event: {
                data: {id: eventId},
            },
            searchRequest: {monthly, search_string: searchQuery, pageType},
        } = state;

        const isEvent = Boolean(eventId);
        const isEventPackage = pageType === Page.EVENT_PACKAGES_SEARCH;
        const {searchUUID} = SearchTracking.getValues();
        const parkingType = getParkingType({
            isMonthly: monthly,
            isAirport: airport,
            isEvent,
            isEventPackage,
        });

        return {
            ...coreProperties,
            city: citySlug,
            pageType,
            parkingType,
            query: searchQuery,
            searchId: searchUUID,
        };
    } catch (error) {
        ErrorUtils.sendSentryMessage({
            customErrorMessage:
                'SpotListNoResults: Error getting segment properties',
            error,
        });

        return coreProperties;
    }
};

const SpotListNoResults = ({numFilters, onResetFiltersClick}) => {
    const isMobile = useBreakpointValue({base: true, tablet: false});
    const listItemStyles = {
        'list-style-position': 'outside',
    };
    const ContainerComponent = isMobile ? Box : Card;
    const state = useSelector(s => s);

    useEffect(() => {
        const properties = getSegmentProperties(state, numFilters);

        trackErrorMessageDisplayed(properties);
    }, [numFilters, state]);

    if (numFilters > 0) {
        return (
            <NoResultsForFiltersMessage
                onResetFiltersClick={onResetFiltersClick}
            />
        );
    }

    return (
        <ContainerComponent>
            <Flex justifyContent="center">
                <Image
                    src="/img/search/spothero-magnifying-glass.svg"
                    alt="Magnifying glass"
                />
            </Flex>

            <Box padding={2}>
                <Text variant="body2" fontWeight="semibold" paddingY={2}>
                    We couldn&apos;t find any available spots nearby.
                </Text>

                <Text variant="body2" paddingBottom={2}>
                    Don&apos;t worry though, we&apos;ve got a few suggestions to
                    help you out:
                </Text>
                <List type="ordered" marginLeft={4}>
                    <ListItem sx={listItemStyles}>
                        Try adjusting the dates and times you&apos;ve selected.
                    </ListItem>
                    <ListItem sx={listItemStyles}>
                        If you zoom out on the map, you might find results just
                        outside your initial search area.
                    </ListItem>
                    <ListItem sx={listItemStyles}>
                        Consider typing in a different location.
                    </ListItem>
                </List>
            </Box>
        </ContainerComponent>
    );
};

SpotListNoResults.propTypes = {
    numFilters: PropTypes.number,
    onResetFiltersClick: PropTypes.func,
};

export default SpotListNoResults;
